/**
 * Shared API Utility for Origo components.
 * Handles deduplication and caching of API requests.
 */
window.OrigoApi = (function () {
    const CACHE_PREFIX = 'origo_api_cache_';
    const DEFAULT_TTL = 3600000; // 1 hour in milliseconds
    const pendingRequests = new Map();

    /**
     * Fetch with retries and timeout.
     * @param {string} url
     * @param {object} options
     * @param {number} retries
     * @param {number} delay
     */
    function fetchWithRetries(url, options = {}, retries = 1, delay = 500) {
        const controller = new AbortController();
        const signal = controller.signal;
        const fetchOptions = { ...options, signal };

        // Default timeout of 3s if not specified
        const timeout = options.timeout || 3000;
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        return fetch(url, fetchOptions)
            .finally(() => clearTimeout(timeoutId))
            .then(res => {
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                return res;
            })
            .catch(err => {
                if (retries > 0 && err.name !== 'AbortError') {
                    return new Promise(resolve => setTimeout(resolve, delay))
                        .then(() => fetchWithRetries(url, options, retries - 1, delay * 1.5));
                }
                throw err;
            });
    }

    /**
     * Fetch JSON data with caching and deduplication.
     * @param {string} url
     * @param {number} ttl - Time to live in milliseconds (default 1 hour)
     */
    function fetchCached(url, ttl = DEFAULT_TTL) {
        // 1. Check LocalStorage
        const cacheKey = CACHE_PREFIX + url;
        const cached = localStorage.getItem(cacheKey);

        if (cached) {
            try {
                const record = JSON.parse(cached);
                const now = Date.now();
                if (record.expiry > now) {
                    // Return cached data as a Promise
                    return Promise.resolve(record.data);
                } else {
                    // Expired
                    localStorage.removeItem(cacheKey);
                }
            } catch (e) {
                // Corrupt cache
                localStorage.removeItem(cacheKey);
            }
        }

        // 2. Check pending requests (Deduplication)
        if (pendingRequests.has(url)) {
            return pendingRequests.get(url);
        }

        // 3. New Request
        const requestPromise = fetchWithRetries(url, { cache: 'no-store' })
            .then(res => res.json())
            .then(data => {
                // Save to cache
                try {
                    const record = {
                        data: data,
                        expiry: Date.now() + ttl
                    };
                    localStorage.setItem(cacheKey, JSON.stringify(record));
                } catch (e) {
                    console.warn('OrigoApi: Failed to write to localStorage', e);
                }
                return data;
            })
            .finally(() => {
                // Remove from pending map when done
                pendingRequests.delete(url);
            });

        pendingRequests.set(url, requestPromise);
        return requestPromise;
    }

    return {
        fetchWithRetries,
        fetchCached
    };
})();
